import { useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "../../components/PageHeader";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { SearchableCombobox } from "../../components/ui/SearchableCombobox";
import { DateField } from "../../components/ui/DateField";
import {
  CalendarClock,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  DoorOpen,
  Eye,
  LogIn,
  LogOut,
  QrCode,
  RefreshCw,
  ScanLine,
  UserPlus,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { useAccess } from "../../contexts/AccessContext";
import { isAdminRole } from "../../utils/rolePermissions";
import { getEmployees } from "../../api/employees";
import {
  checkInVisitor,
  checkOutVisitor,
  getVisitPurposes,
  getVisitorDashboard,
  getVisitors,
  type Visitor,
  type VisitorDashboard,
  type VisitorStatus,
} from "../../api/visitors";
import VisitorDetailDialog from "./VisitorDetailDialog";
import VisitorFormDialog, { type EmployeeOption } from "./VisitorFormDialog";
import VisitorPassDialog from "./VisitorPassDialog";
import VisitorScanner from "./VisitorScanner";
import {
  STATUS_BADGE,
  STATUS_LABEL,
  STATUS_ORDER,
  errMsg,
  fmtClock,
  fmtDate,
  fmtTimeOnly,
  initials,
  visitDuration,
} from "./visitorUi";

type TabKey = "dashboard" | "register" | "history";
const PAGE_SIZE = 12;

/**
 * Visitor Management.
 *
 * Four sections, matching how the module is actually used:
 *   • Dashboard — today's desk: expected, inside now, upcoming, recent scans.
 *   • Visitors  — the live register with search and filters.
 *   • Scanner   — the reception/security QR desk (Manage only).
 *   • History   — past visits and their full entry/exit logs.
 *
 * "Create Visitor" is a dialog reachable from every section rather than a tab
 * of its own: inviting someone is a 20-second interruption of whatever the
 * user was looking at, and sending them to a separate page loses that place.
 *
 * Permissions follow Access Management's `visitors` panel. Manage is the front
 * desk (the whole branch register, the scanner, check-in/out); everyone else
 * gets self-service — invite the visitors you host and track them. The backend
 * enforces the same split, so a wrongly granted button surfaces as a 403
 * rather than as data.
 */
export default function Visitors() {
  const { user } = useAuth();
  const { canManage, hasAccess } = useAccess();

  // With NO Access profile assigned, canManage() answers a permissive `true`
  // for every panel — that is what keeps admins working (they have no Employee
  // row, so they carry no profile). Taken alone it would hand a plain employee
  // the scanner and the branch-wide register, neither of which the backend
  // allows them. So with no profile, fall back to the role the way the
  // backend's can(..., roles=ELEVATED_ROLES) does.
  const manageAllowed = canManage("visitors") && (hasAccess || isAdminRole(user?.role));

  const [tab, setTab] = useState<TabKey>("dashboard");
  // The scanner is a dialog, not a tab: "Scan Pass" is something the desk
  // reaches for mid-conversation with a visitor standing in front of them, and
  // it should not cost them the list they were looking at.
  const [scannerOpen, setScannerOpen] = useState(false);
  const [dash, setDash] = useState<VisitorDashboard | null>(null);
  const [rows, setRows] = useState<Visitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [purposes, setPurposes] = useState<string[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);

  // Filters (register + history)
  const [statusFilter, setStatusFilter] = useState<VisitorStatus | "">("");
  const [hostFilter, setHostFilter] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState<string | null>(null);
  const [dateTo, setDateTo] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  // Dialogs
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Visitor | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [passId, setPassId] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);

  const scope = tab === "history" ? "history" : "all";

  const loadDashboard = useCallback(async () => {
    try {
      setDash(await getVisitorDashboard());
    } catch (e) {
      toast.error(errMsg(e, "Could not load the visitor dashboard"));
    }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getVisitors({
        scope: scope as any,
        status: statusFilter || "",
        q: search || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        limit: 500,
      });
      setRows(res.visitors || []);
    } catch (e) {
      toast.error(errMsg(e, "Could not load visitors"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [scope, statusFilter, search, dateFrom, dateTo]);

  const refresh = useCallback(() => {
    loadDashboard();
    if (tab === "register" || tab === "history") loadRows();
  }, [loadDashboard, loadRows, tab]);

  useEffect(() => {
    loadDashboard().finally(() => setLoading(false));
  }, [loadDashboard]);

  useEffect(() => {
    if (tab === "register" || tab === "history") loadRows();
  }, [tab, loadRows]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, hostFilter, search, dateFrom, dateTo, tab]);

  // Purposes + the host pick-list are only needed by the form; a plain
  // employee hosts their own visitors and never sees the employee picker, so
  // the (role-scoped) employee fetch is skipped for them entirely.
  useEffect(() => {
    if (!formOpen) return;
    if (!purposes.length) {
      getVisitPurposes()
        .then(setPurposes)
        .catch(() => setPurposes([]));
    }
    if (manageAllowed && !employees.length) {
      getEmployees()
        .then((r) => {
          const list = Array.isArray(r?.employees) ? r.employees : [];
          setEmployees(
            list.map((e: any) => ({
              id: e.id,
              label: `${[e.first_name, e.last_name].filter(Boolean).join(" ") || e.email || e.id}${
                e.employeeID ? ` (${e.employeeID})` : ""
              }`,
            }))
          );
        })
        .catch(() => setEmployees([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formOpen, manageAllowed]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const quickAct = async (v: Visitor, action: "in" | "out") => {
    setActingId(v.id);
    try {
      if (action === "in") await checkInVisitor(v.id);
      else await checkOutVisitor(v.id);
      toast.success(action === "in" ? "Checked in" : "Checked out");
      refresh();
    } catch (e) {
      toast.error(errMsg(e, "That action was refused"));
    } finally {
      setActingId(null);
    }
  };

  // ---- derived lists -------------------------------------------------------

  const hostOptions = useMemo(
    () =>
      Array.from(
        new Set(rows.map((r) => r.host_name).filter(Boolean) as string[])
      ).sort(),
    [rows]
  );

  const nameOptions = useMemo(
    () =>
      Array.from(
        new Set(rows.map((r) => r.visitor_name).filter(Boolean))
      ).sort(),
    [rows]
  );

  // Host is filtered client-side (the API filters by employee id, and this
  // picker offers the names already on screen); everything else is server-side.
  const filtered = useMemo(
    () => (hostFilter ? rows.filter((r) => r.host_name === hostFilter) : rows),
    [rows, hostFilter]
  );

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const filtersActive =
    !!statusFilter || !!hostFilter || !!search || !!dateFrom || !!dateTo;
  const clearFilters = () => {
    setStatusFilter("");
    setHostFilter("");
    setSearch("");
    setDateFrom(null);
    setDateTo(null);
  };

  const TABS: { key: TabKey; label: string }[] = [
    { key: "dashboard", label: "Dashboard" },
    { key: "register", label: "Visitors" },
    { key: "history", label: "History" },
  ];

  // ---- small renderers ----------------------------------------------------

  const visitorCell = (v: Visitor) => (
    <div className="flex items-center gap-3">
      {v.photo_url ? (
        <img
          src={v.photo_url}
          alt=""
          className="h-9 w-9 rounded-full bg-gray-100 object-cover"
        />
      ) : (
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">
          {initials(v.visitor_name)}
        </div>
      )}
      <div className="min-w-0">
        <div className="truncate font-medium text-gray-900">
          {v.visitor_name}
          {v.visitor_count > 1 && (
            <span className="ml-1 text-xs text-gray-500">+{v.visitor_count - 1}</span>
          )}
        </div>
        <div className="truncate text-xs text-gray-500">
          {v.company_name || v.visitor_phone || "—"}
        </div>
      </div>
    </div>
  );

  const rowActions = (v: Visitor) => (
    <div className="flex items-center justify-end gap-1">
      {manageAllowed && v.status === "INVITED" && (
        <button
          className="gt-btn is-ghost is-sm"
          disabled={actingId === v.id}
          onClick={() => quickAct(v, "in")}
          title="Check in without scanning"
        >
          <LogIn size={14} /> In
        </button>
      )}
      {manageAllowed && v.status === "CHECKED_IN" && (
        <button
          className="gt-btn is-ghost is-sm"
          disabled={actingId === v.id}
          onClick={() => quickAct(v, "out")}
          title="Check out without scanning"
        >
          <LogOut size={14} /> Out
        </button>
      )}
      <button
        className="gt-btn is-ghost is-sm"
        onClick={() => setPassId(v.id)}
        title="Show the QR pass"
      >
        <QrCode size={14} />
      </button>
      <button
        className="gt-btn is-ghost is-sm"
        onClick={() => setDetailId(v.id)}
        title="Open the visit"
      >
        <Eye size={14} />
      </button>
    </div>
  );

  const compactList = (list: Visitor[], empty: string, showEntry = false) =>
    !list.length ? (
      <div className="py-8 text-center text-sm text-gray-500">{empty}</div>
    ) : (
      <ul className="divide-y divide-gray-100">
        {list.map((v) => (
          <li key={v.id} className="flex items-center gap-3 py-2.5">
            <div className="min-w-0 flex-1">{visitorCell(v)}</div>
            <div className="hidden shrink-0 text-xs text-gray-500 sm:block">
              {v.host_name || "—"}
            </div>
            <div className="shrink-0 text-xs text-gray-500">
              {showEntry
                ? `in ${fmtTimeOnly(v.checked_in_at)}${
                    visitDuration(v) ? ` · ${visitDuration(v)}` : ""
                  }`
                : fmtClock(v.expected_time)}
            </div>
            {rowActions(v)}
          </li>
        ))}
      </ul>
    );

  const pager = (
    <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
      <span>
        {filtered.length === 0
          ? "No visits"
          : `${(safePage - 1) * PAGE_SIZE + 1}–${Math.min(
              safePage * PAGE_SIZE,
              filtered.length
            )} of ${filtered.length}`}
      </span>
      <div className="flex items-center gap-1">
        <button
          className="gt-btn is-ghost is-sm"
          disabled={safePage <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          <ChevronLeft size={14} />
        </button>
        <span>
          {safePage} / {pageCount}
        </span>
        <button
          className="gt-btn is-ghost is-sm"
          disabled={safePage >= pageCount}
          onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );

  const filterBar = (
    <div className="flex w-full flex-wrap items-center gap-2">
      <div style={{ flex: "1 1 11rem", minWidth: 0 }}>
        <SearchableCombobox
          value={search}
          onChange={setSearch}
          options={nameOptions}
          placeholder="Search visitors…"
          searchPlaceholder="Name, phone, company, pass code…"
          emptyText="No data found."
          allowCustom
          matchTriggerWidth
          className="h-auto px-3 py-1.5"
        />
      </div>
      <div style={{ flex: "1 1 10rem", minWidth: 0 }}>
        <SearchableCombobox
          value={hostFilter || "All Hosts"}
          onChange={(v) => setHostFilter(v === "All Hosts" ? "" : v)}
          options={["All Hosts", ...hostOptions]}
          placeholder="All Hosts"
          searchPlaceholder="Search hosts…"
          emptyText="No data found."
          matchTriggerWidth
          className="h-auto px-3 py-1.5"
        />
      </div>
      <div style={{ flex: "1 1 9.5rem", minWidth: 0 }}>
        <SearchableCombobox
          value={statusFilter ? STATUS_LABEL[statusFilter] : "All Status"}
          onChange={(label) =>
            setStatusFilter(
              label === "All Status"
                ? ""
                : ((STATUS_ORDER.find((s) => STATUS_LABEL[s] === label) ||
                    "") as VisitorStatus | "")
            )
          }
          options={["All Status", ...STATUS_ORDER.map((s) => STATUS_LABEL[s])]}
          placeholder="All Status"
          searchPlaceholder="Search status…"
          emptyText="No data found."
          matchTriggerWidth
          className="h-auto px-3 py-1.5"
        />
      </div>
      <div style={{ flex: "0 0 9.5rem" }}>
        <DateField
          value={dateFrom}
          onChange={(v) => setDateFrom(v || null)}
          placeholder="From"
          size="sm"
          maxDate={dateTo}
        />
      </div>
      <div style={{ flex: "0 0 9.5rem" }}>
        <DateField
          value={dateTo}
          onChange={(v) => setDateTo(v || null)}
          placeholder="To"
          size="sm"
          minDate={dateFrom}
        />
      </div>
      {filtersActive && (
        <button className="gt-btn is-ghost is-sm shrink-0" onClick={clearFilters}>
          Clear
        </button>
      )}
      <button
        className="gt-btn is-ghost is-sm shrink-0"
        onClick={refresh}
        title="Refresh"
      >
        <RefreshCw size={14} />
      </button>
    </div>
  );

  const registerTable = (
    <CardContent>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
        </div>
      ) : pageRows.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500">
          <Users className="mx-auto mb-2 h-10 w-10 text-gray-300" />
          {filtersActive
            ? "No visits match these filters."
            : tab === "history"
              ? "No past visits recorded yet."
              : "No visitors yet. Create one to generate a QR pass."}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500">
                  <th className="py-2 pr-3">Visitor</th>
                  <th className="py-2 pr-3">Host</th>
                  <th className="py-2 pr-3">Purpose</th>
                  <th className="py-2 pr-3">Scheduled</th>
                  <th className="py-2 pr-3">Entry</th>
                  <th className="py-2 pr-3">Exit</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pageRows.map((v) => (
                  <tr key={v.id} className="transition-colors hover:bg-gray-50">
                    <td className="py-3 pr-3">{visitorCell(v)}</td>
                    <td className="py-3 pr-3">
                      <div className="text-gray-900">{v.host_name || "—"}</div>
                      <div className="text-xs text-gray-500">
                        {v.host_department || v.host_employeeID || ""}
                      </div>
                    </td>
                    <td className="py-3 pr-3 text-gray-700">{v.purpose || "—"}</td>
                    <td className="py-3 pr-3 text-gray-700">
                      <div>{fmtDate(v.visit_date)}</div>
                      <div className="text-xs text-gray-500">
                        {fmtClock(v.expected_time)}
                      </div>
                    </td>
                    <td className="py-3 pr-3 text-gray-700">
                      {fmtTimeOnly(v.checked_in_at)}
                    </td>
                    <td className="py-3 pr-3 text-gray-700">
                      {fmtTimeOnly(v.checked_out_at)}
                    </td>
                    <td className="py-3 pr-3">
                      <Badge className={`rounded-md ${STATUS_BADGE[v.status]}`}>
                        {STATUS_LABEL[v.status]}
                      </Badge>
                    </td>
                    <td className="py-3 pr-3 text-right">{rowActions(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pager}
        </>
      )}
    </CardContent>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={<>Visitor <em>Management</em></>}
        subtitle={
          manageAllowed ? (
            <>Invite, verify and track <em>every visitor</em> at the gate.</>
          ) : (
            <>Invite visitors and track <em>who is coming to see you</em>.</>
          )
        }
        actions={
          <>
            {manageAllowed && (
              <button
                className="gt-btn"
                onClick={() => setScannerOpen(true)}
                title="Open the reception scanner"
              >
                <ScanLine size={16} /> Scan Pass
              </button>
            )}
            <button className="gt-btn is-primary" onClick={openCreate}>
              <UserPlus size={16} /> Create Visitor
            </button>
          </>
        }
      />

      {/* Stat tiles — today's desk, scoped the same way the lists are. */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className="gt-stat">
          <div className="gt-stat-label">
            <CalendarClock size={14} /> Expected Today
          </div>
          <div className="gt-stat-value">{dash?.summary.expected ?? "—"}</div>
        </div>
        <div className="gt-stat">
          <div className="gt-stat-label">
            <DoorOpen size={14} /> Inside Now
          </div>
          <div className="gt-stat-value">{dash?.summary.inside_now ?? "—"}</div>
        </div>
        <div className="gt-stat">
          <div className="gt-stat-label">
            <CheckCircle2 size={14} /> Checked Out Today
          </div>
          <div className="gt-stat-value">{dash?.summary.checked_out_today ?? "—"}</div>
        </div>
        <div className="gt-stat">
          <div className="gt-stat-label">
            <Users size={14} /> People Today
          </div>
          <div className="gt-stat-value">{dash?.summary.today_people ?? "—"}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`rounded-md border-b-2 px-3 py-1.5 text-sm transition-colors ${
              tab === t.key
                ? "border-emerald-600 font-semibold text-emerald-700"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "dashboard" ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <DoorOpen size={16} /> Inside now
                {dash?.inside.length ? (
                  <Badge className="rounded-md bg-green-100 text-green-700">
                    {dash.inside.length}
                  </Badge>
                ) : null}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {compactList(dash?.inside || [], "Nobody is on the premises.", true)}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <CalendarClock size={16} /> Expected today
              </CardTitle>
            </CardHeader>
            <CardContent>
              {compactList(
                (dash?.today || []).filter((v) => v.status === "INVITED"),
                "No visitors are expected today."
              )}
            </CardContent>
          </Card>

          {/* The "Checked Out Today" tile used to be a dead end — the number
              counted visits that had left, and no list on the page showed
              them (Expected Today is INVITED-only, Inside Now is current).
              This is the list behind that number. */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <CheckCircle2 size={16} /> Checked out today
              </CardTitle>
            </CardHeader>
            <CardContent>
              {compactList(
                (dash?.today || []).filter((v) => v.status === "CHECKED_OUT"),
                "No visitor has checked out today.",
                true
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Upcoming visits</CardTitle>
            </CardHeader>
            <CardContent>
              {compactList(dash?.upcoming || [], "Nothing booked beyond today.")}
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card>
          <CardHeader className="space-y-3">
            <CardTitle className="text-base">
              {tab === "history" ? "Visitor history" : "Visitor register"}
            </CardTitle>
            {filterBar}
          </CardHeader>
          {registerTable}
        </Card>
      )}

      <VisitorFormDialog
        open={formOpen}
        editing={editing}
        purposes={purposes}
        employees={employees}
        canManage={manageAllowed}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSaved={(saved, created) => {
          refresh();
          // Straight to the pass after an invite: the whole point is to send
          // the visitor their QR, and making the user hunt for it afterwards is
          // where this flow would lose people.
          if (created) setPassId(saved.id);
        }}
      />

      <VisitorDetailDialog
        visitorId={detailId}
        canManage={manageAllowed}
        onClose={() => setDetailId(null)}
        onChanged={refresh}
        onEdit={(v) => {
          setDetailId(null);
          setEditing(v);
          setFormOpen(true);
        }}
        onShowPass={(id) => setPassId(id)}
      />

      <VisitorPassDialog visitorId={passId} onClose={() => setPassId(null)} />

      {/* Mounted only for the desk: the camera belongs to whoever can actually
          check people in. */}
      {manageAllowed && (
        <VisitorScanner
          open={scannerOpen}
          onClose={() => setScannerOpen(false)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}
